import { EnvConfiguration } from '../config/EnvConfiguration';
import { MediaTokenProvider } from './MediaTokenProvider';
import { buildAuthorizedMediaUrl } from './buildAuthorizedMediaUrl';

// Turns a stored media url into the url to actually fetch. Attachments and member photos both sit
// on the public uploads bucket and both now have an authorised, audited route in front of them;
// this is the one place that decides which to use, so the two cannot drift.
//
// The credential travels as a query parameter because the consumers are an <img> tag and a CSS
// background-image, neither of which can set headers — AuthMiddleware accepts it there for exactly
// that reason. It is deliberately a media-scoped token rather than the session's access token: a
// url reaches browser history, referrer headers, access logs and session replay (which masks text
// only, so it records image urls in full), and this token reaches only the media routes, for
// whatever media the principal could already read. It is not scoped to the single
// image it was minted for, so it is the short expiry rather than the scope that limits a leaked one.
export class AuthorizedMediaUrlResolver {

    static inject = () => [MediaTokenProvider, EnvConfiguration];
    constructor(
        private mediaTokenProvider: MediaTokenProvider,
        private config: EnvConfiguration
    ) {}

    // THROWS when a link was offered and no credential could be obtained. It used to fall back to
    // the stored bucket url, which silently turned every failed mint into an unaudited read of a
    // public object — the exact thing the download route exists to stop. There is no pass-through
    // left except the honest one: no link offered at all, meaning the server never had an audited
    // route to point at: an object we do not host (a social login's account picture), or anything
    // served by a deploy older than the route.
    //
    // Callers must therefore handle a rejection. Doing nothing with it is worse than the old
    // fallback: useResolvedMediaUrl rethrows into the nearest error boundary, and NoteAttachment's
    // click handlers are not awaited by Href, so an uncaught one is an unhandled rejection that
    // tells nobody. Both catch, report to the caregiver and to Sentry, and degrade.
    resolve = async (params: { url: string; href?: string }): Promise<string> => {
        const { url, href } = params;

        // Skipped when no link was offered, so those cost no token fetch. The server offers one
        // only where it can actually serve the object, so the absence of a link is a decision it
        // has already made rather than something to second-guess here.
        const token = href ? await this.mediaTokenProvider.get() : undefined;

        return buildAuthorizedMediaUrl({ url, href, token, apiBaseUrl: this.config.API_BASE_URL });
    };
}
